import { IconsType } from '@monorepo/components'
import * as S from './styles'
import { MouseEvent } from 'react'
import moment from 'moment'
import { FileTransferHelper } from '../../../helpers/file-transfer-helper'
import { useKeycloak } from '@react-keycloak/web'
import { KeycloakHelper } from '@monorepo/infra'
import { MediaTileConfigHelper } from '../../../helpers/media-tile-config-helper'
import { HomepageCarouselHelper } from '../../../helpers/homepage-carousel-helper'

interface Card {
  title: string
  link: string
  iconName: keyof typeof IconsType
  external?: boolean
  handleDownloadClick?: (link: string, event?: MouseEvent) => Promise<void>
  isForbid?: boolean
}

interface CardGroup {
  subtitle: string
  cards: Card[]
}

interface DealerFileMetaDataSchema {
  uploadDate?: string
}

interface DealerFileMetaData {
  uploadDate: Date | null
}

const mapSchemaToDealerFileMetaData = (
  schema: DealerFileMetaDataSchema
): DealerFileMetaData => {
  return {
    uploadDate: tryDeserializeUploadDate(schema.uploadDate),
  }
}

const tryDeserializeUploadDate = (dateStr?: string): Date | null => {
  if (!dateStr || dateStr.trim().length === 0) {
    return null
  }

  const momentUSDate = moment(dateStr, 'MM-DD-YYYY')

  if (momentUSDate.isValid()) {
    return momentUSDate.toDate()
  }

  const momentDate = moment(dateStr)

  return momentDate.isValid() ? momentDate.toDate() : null
}

const loadDealerFileMetaDataAsync =
  async (token?: string): Promise<DealerFileMetaData | null> => {
    const dealerFileMetaDataUrl = `${process.env.REACT_APP_REST_USER_BASE_URL}/dealer/dealerfile/${encodeURIComponent('metadata.json')}`

    try {
      const headers: HeadersInit = token
        ? { Authorization: `Bearer ${token}` }
        : {}

      const dealerFileMetaDataResponse = await fetch(dealerFileMetaDataUrl, {
        headers,
        credentials: 'include',
      })

      if (!dealerFileMetaDataResponse.ok) {
        return null
      }

      const dealerFileMetaDataSchema = await dealerFileMetaDataResponse.json()
      return mapSchemaToDealerFileMetaData(dealerFileMetaDataSchema)
    } catch (error) {
      console.error(error)
    }

    return null
  }

const getDealerFileDownloadFileName = (uploadDate: Date): string => {
  const dateStr = moment(uploadDate).format('MMDDYYYY')
  return `DealerFile${dateStr}.csv`
}

const getDealerFileDownloadFileNameAsync = async (token?: string): Promise<string> => {
  const dealerFileMetaData = await loadDealerFileMetaDataAsync(token)
  return getDealerFileDownloadFileName(
    dealerFileMetaData?.uploadDate ?? new Date()
  )
}

const getMenuCards = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  user?: any,
  handleDownloadClickAsync?: (link: string, event?: MouseEvent) => Promise<void>
): CardGroup[] => {
  return [
    {
      subtitle: 'People',
      cards: [
        {
          title: 'Directory',
          iconName: 'PersonCropRectangleStack',
          link: '/admin/directory',
        },
        {
          title: 'Export Dealer File',
          iconName: 'ExportIcon',
          link: `${process.env.REACT_APP_REST_USER_BASE_URL}/dealer/dealerfile/${encodeURIComponent('DealerFile.csv?v=1762447410755')}`,
          external: true,
          handleDownloadClick: handleDownloadClickAsync,
        },
        {
          title: 'File Transfer',
          iconName: 'FileTransferIcon',
          link: `/admin/file-transfer`,
          isForbid: !FileTransferHelper.isAllowedUser(user),
        },
        {
          title: 'Drive',
          iconName: 'FileTransferIcon',
          link: '/admin/drive',
        },
      ],
    },
    {
      subtitle: 'Storefront Controls',
      cards: [
        {
          title: 'Resources',
          iconName: 'BooksVertical',
          link: '/admin/resources',
        },
        {
          title: 'New User Pop-Up',
          iconName: 'Megaphone',
          link: '/admin/pop-ups',
        },
        {
          title: 'Message Blasts',
          iconName: 'Envelope',
          link: '/admin/message-blasts',
        },
        {
          title: 'Homepage Carousal',
          iconName: 'HomepageImage',
          link: '/admin/homepage-image',
          isForbid: !HomepageCarouselHelper.isAllowedUser(user)
        },
        {
          title: 'Media Tiles',
          iconName: 'MediaTileIcon',
          link: '/admin/media-tile-configs',
          isForbid: !MediaTileConfigHelper.isAllowedUser(user),
        },
      ],
    },
  ]
}

const downloadDealerFile = async (token?: string): Promise<void> => {
  try {
    const url = `${process.env.REACT_APP_REST_USER_BASE_URL}/dealer/dealerfile/${encodeURIComponent(
      'DealerFile.csv'
    )}`

    const headers: HeadersInit = token
      ? { Authorization: `Bearer ${token}` }
      : {}

    const res = await fetch(url, {
      method: 'GET',
      headers,
      credentials: 'include',
    })

    if (!res.ok) {
      console.error('Failed to download')
      return
    }

    const blob = await res.blob()
    const href = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = href
    a.download = await getDealerFileDownloadFileNameAsync(token)
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(href)
  } catch (error) {
    console.error(error)
  }
}

const SectionAdminMenus: React.FC = () => {
  const { keycloak } = useKeycloak()
  const user = KeycloakHelper.getTokenParsed(keycloak)
  const handleDownloadClickAsync = async (
    _link: string,
    event?: MouseEvent
  ): Promise<void> => {
    event?.preventDefault()
    await downloadDealerFile(keycloak?.token)
  }
  const menuCards = getMenuCards(user, handleDownloadClickAsync)

  return (
    <S.Container>
      {menuCards.map(({ subtitle, cards }, index) => (
        <S.SectionContainer key={index}>
          <S.Subtitle>{subtitle}</S.Subtitle>
          <S.CardsContainer>
            {cards
              .filter((x) => !x.isForbid)
              .map(
                (
                  { iconName, title, link, external, handleDownloadClick },
                  index
                ) => (
                  <S.Card
                    to={external ? { pathname: link } : link}
                    target={external ? '_blank' : undefined}
                    key={index}
                    onClick={
                      handleDownloadClick
                        ? (event) => handleDownloadClick(link, event)
                        : undefined
                    }
                  >
                    <S.CardTitleContainer>
                      <S.Icon icon={iconName} size={50} hasFill={false} />
                      <S.CardTitle>{title}</S.CardTitle>
                    </S.CardTitleContainer>
                    <S.CardDescription></S.CardDescription>
                  </S.Card>
                )
              )}
          </S.CardsContainer>
        </S.SectionContainer>
      ))}
    </S.Container>
  )
}

export default SectionAdminMenus
